import { RubyGemMark } from '../../components/RubyGemMark';
import React, { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import type { UnitStatus } from '@platform/shared-types';
import {
  hardwareApi, EXCEPTION_FLOW, REASON_REQUIRED, UNIT_STATUS_LABEL, unitTransitionBlock,
  type DeviceUnitRow,
} from '../../services/hardwareApi';
import { toastService } from '../../services/toastService';
import {
  ProblemMenu, DeviceSteps, nextStepHint, pausedAt, resumeStateOf,
} from './DeviceTracker';

/** The problem states, straight off the shared vocabulary. */
const EXCEPTIONS = new Set<UnitStatus>(EXCEPTION_FLOW);

/**
 * WHERE THIS DEVICE IS, AND THE ONE THING A PERSON STILL DECIDES ABOUT IT.
 *
 * The order screen has had this for a while — the eight-step rail and the
 * problem menu — but only there, so a problem discovered at the bench meant
 * leaving the bench, finding the order, and coming back. Out of stock,
 * backordered, RMA, lost: all facts learned while holding the device.
 *
 * DRAWN FROM THE SAME PARTS, not copied. `DeviceSteps` and `ProblemMenu` are
 * the order screen's own components; a second rail would have stopped matching
 * the states it draws the first time one was added.
 *
 * THE DIFFERENCE IS THE SAVE POLICY, and it is deliberate. The order screen
 * stages every device's change into a draft and saves them together, because
 * it is reviewing a whole shipment. The bench holds ONE device and has just
 * learned one fact about it, so the pick is written immediately — a draft
 * nobody remembers to save is how a device sits marked RMA in one person's
 * browser and fine in everyone else's.
 */
const DeviceStatusPanel: React.FC<{
  unit: DeviceUnitRow;
  orderStatus?: string;
  /** The updated unit, so the caller patches its row instead of refetching. */
  onUnitChanged: (unit: DeviceUnitRow) => void;
}> = ({ unit, orderStatus, onUnitChanged }) => {
  const [busy, setBusy] = useState(false);
  /** A pick waiting on its reason. Null when nothing is being asked. */
  const [asking, setAsking] = useState<UnitStatus | null>(null);
  const [reason, setReason] = useState('');

  const state = unit.state as UnitStatus;
  /* Read off EXCEPTION_FLOW, never listed by hand. A hand-written copy is how
     `lost` would have been missing here while appearing in the menu two lines
     below — the same drift this whole file exists to avoid. */
  const problem = EXCEPTIONS.has(state) || state === 'in_stock';

  const apply = async (next: UnitStatus, holdReason?: string) => {
    /* The SAME guard the order screen checks before offering the pick. Asked
       again here because this is a different screen reaching the same
       endpoint, and a rule enforced in only one caller is not a rule. */
    const blocked = unitTransitionBlock(unit, next, orderStatus as any);
    if (blocked) {
      toastService.error(blocked);
      return;
    }
    setBusy(true);
    try {
      const res = await hardwareApi.setUnitStatus(unit.unitId, {
        state: next,
        ...(holdReason ? { holdReason } : {}),
      } as any);
      if (res?.unit) onUnitChanged(res.unit);
      toastService.success(
        next === 'in_stock'
          ? `${unit.trackingId || unit.unitId} is back in stock and off this order.`
          : `${unit.trackingId || unit.unitId} is now ${UNIT_STATUS_LABEL[next]}.`,
      );
    } catch (e: any) {
      // The server's own message names what to do instead — surfaced verbatim
      // rather than replaced with a generic failure.
      toastService.error(e?.message || 'Could not update this device.');
    } finally {
      setBusy(false);
      setAsking(null);
      setReason('');
    }
  };

  const pick = (next: UnitStatus) => {
    /* On hold, backordered and lost are shown TO THE MERCHANT, so they cannot
       be recorded without saying why. Asked in a real dialog rather than a
       native prompt, which does not exist in the iOS webview this app also
       has to run in. */
    if (REASON_REQUIRED.has(next)) {
      setAsking(next);
      setReason(unit.holdReason || '');
      return;
    }
    apply(next);
  };

  const { next, hint } = nextStepHint(unit, state);

  return (
    <div className="rounded-xl border border-stone-200 bg-white p-3">
      <div className="mb-2.5 flex flex-wrap items-center justify-between gap-2">
        <span className="text-[10px] font-bold uppercase tracking-wider text-stone-400">
          Where this device is
        </span>
        <div className="flex items-center gap-2">
          {busy && <RubyGemMark size={13} drawing />}
          <ProblemMenu unit={unit} current={state} orderStatus={orderStatus} onPick={pick} />
        </div>
      </div>

      <DeviceSteps unit={unit} state={state} compact />

      {!problem && hint && (
        <p className="mt-2 text-[11.5px] text-stone-600">
          {next && <>Next: <b className="text-stone-900">{next}</b> — </>}
          {hint}
        </p>
      )}

      {problem && (
        <div className="mt-2 flex flex-wrap items-center gap-2 rounded-lg border border-red-200 bg-red-50/60 px-3 py-2">
          <AlertTriangle size={13} className="shrink-0 text-red-600" />
          <p className="min-w-[200px] flex-1 text-[11.5px] text-stone-700">
            <b className="text-red-700">{UNIT_STATUS_LABEL[state]}</b>
            {state === 'in_stock'
              ? ' — this device has come off the order.'
              : ` — progress is paused at ${pausedAt(unit, state)}.`}
            {unit.holdReason ? ` · ${unit.holdReason}` : ''}
          </p>
          <button
            type="button"
            onClick={() => apply(resumeStateOf(unit))}
            disabled={busy}
            className="inline-flex min-h-[32px] items-center rounded-lg border border-emerald-300 bg-white px-2.5 text-[11px] font-bold text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
          >
            Clear problem
          </button>
        </div>
      )}

      {/* THE REASON, ASKED PROPERLY. Custom dialog: no window.prompt, which
          breaks in the iOS webview and cannot be styled or tested. */}
      {asking && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setAsking(null)}>
          <div className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-[15px] font-bold text-stone-900">
              Why is this {UNIT_STATUS_LABEL[asking].toLowerCase()}?
            </h3>
            <p className="mt-1 text-[12px] text-stone-500">
              The merchant is shown this reason, so write it for them.
            </p>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              autoFocus
              placeholder="e.g. waiting on the vendor’s next shipment"
              className="mt-3 w-full rounded-xl border border-stone-300 p-2.5 text-[13px] focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
            />
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setAsking(null)}
                className="min-h-[44px] rounded-xl border border-stone-300 bg-white px-4 text-[13px] font-bold text-stone-700 hover:bg-stone-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!reason.trim() || busy}
                onClick={() => apply(asking, reason.trim())}
                className="inline-flex min-h-[44px] items-center gap-1.5 rounded-xl bg-red-600 px-4 text-[13px] font-bold text-white hover:bg-red-700 disabled:opacity-50"
              >
                {busy && <RubyGemMark size={13} drawing color="currentColor" />}
                Record {UNIT_STATUS_LABEL[asking]}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeviceStatusPanel;
